import { useCallback, useEffect, useRef, useState } from 'react';
import { getSidebarLayoutInfo } from './getSidebarLayoutInfo';

/** Arbitrary threshold which, when the scrollable is smaller in height, the sidebar content is not sticky */
const STICKY_CONTENT_THRESHOLD = 100;

type ScrollTransferSetup = (arg0: {
    target: Element;
    body: Element | null;
    headerHeight: number;
}) => void;

export const useStickyLayout = (container: HTMLDivElement | null) => {
    const [sticky, setSticky] = useState(true);
    const currentSticky = useRef(sticky);

    const scrollTop = useRef<number | null>(null);
    const scrollable = useRef<Element | null>(null);

    // Handle transfer setup for passing the scroll position from the container to the sidebar scrollable body cell
    const transferScrollToBody = useCallback<ScrollTransferSetup>(
        ({ target, body, headerHeight }) => {
            const adjustedScrollTop = target.scrollTop - headerHeight;

            // eslint-disable-next-line no-param-reassign
            target.scrollTop = 0;

            scrollable.current = body;
            scrollTop.current = Math.max(0, adjustedScrollTop);
        },
        []
    );

    // Handle transfer setup for passing the scroll position from the sidebar scrollable body cell to the sidebar container
    const transferScrollToContainer = useCallback<ScrollTransferSetup>(
        ({ target, body, headerHeight }) => {
            if (!body?.scrollTop) {
                return;
            }

            const adjustedScrollTop = body.scrollTop + headerHeight;
            // eslint-disable-next-line no-param-reassign
            body.scrollTop = 0;

            scrollable.current = target;
            scrollTop.current = Math.max(0, adjustedScrollTop);
        },
        []
    );

    const onResize = useCallback<ResizeObserverCallback>(
        (entries) => {
            const entry = entries[0];
            if (!entry) {
                return;
            }

            const containerHeight = entry?.borderBoxSize?.[0]?.blockSize;
            if (!containerHeight) {
                return;
            }

            const { target } = entry;
            const { headerHeight, body, footerHeight } =
                getSidebarLayoutInfo(target);
            const bodyHeight = containerHeight - headerHeight - footerHeight;

            const nextSticky = bodyHeight >= STICKY_CONTENT_THRESHOLD;
            const { current: isSticky } = currentSticky;

            if (!isSticky && nextSticky) {
                // container will transition from non-sticky to sticky header/footer
                transferScrollToBody({ target, body, headerHeight });
            } else if (isSticky && !nextSticky) {
                // container will transition from sticky header/footer to fully scrollable
                transferScrollToContainer({ target, body, headerHeight });
            }

            currentSticky.current = nextSticky;
            setSticky(nextSticky);
        },
        [transferScrollToBody, transferScrollToContainer]
    );

    useEffect(() => {
        if (!scrollable.current || scrollTop.current === null) {
            return;
        }

        scrollable.current.scrollTop = scrollTop.current;
        scrollable.current = null;
        scrollTop.current = null;
    }, [sticky]);

    useEffect(() => {
        if (!container) {
            return undefined;
        }

        if (globalThis.ResizeObserver === undefined) {
            return undefined;
        }

        const observer = new ResizeObserver(onResize);
        observer.observe(container);

        return () => {
            observer.disconnect();
        };
    }, [container, onResize]);

    return sticky;
};
